/**
 * Minimal floating element positioning (flip + shift), for popovers in the top layer.
 *
 * placement: top | bottom | left | right   (side of the anchor)
 * align:     start | center | end           (logical, respects RTL)
 */
export function position(anchor, floating, { placement = 'bottom', align = 'center', offset = 8, padding = 8 } = {}) {
    const rect = anchor.getBoundingClientRect();
    const width = floating.offsetWidth;
    const height = floating.offsetHeight;
    const viewport = { width: document.documentElement.clientWidth, height: window.innerHeight };
    const rtl = getComputedStyle(anchor instanceof Element ? anchor : document.documentElement).direction === 'rtl';

    const space = {
        top: rect.top - padding,
        bottom: viewport.height - rect.bottom - padding,
        left: rect.left - padding,
        right: viewport.width - rect.right - padding,
    };
    const opposite = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
    const needed = placement === 'top' || placement === 'bottom' ? height + offset : width + offset;

    // Flip to the other side when it doesn't fit and the other side has more room.
    let side = placement;
    if (space[side] < needed && space[opposite[side]] > space[side]) {
        side = opposite[side];
    }

    let x;
    let y;

    if (side === 'top' || side === 'bottom') {
        y = side === 'top' ? rect.top - height - offset : rect.bottom + offset;
        const start = rtl ? rect.right - width : rect.left;
        const end = rtl ? rect.left : rect.right - width;
        x = align === 'start' ? start : align === 'end' ? end : rect.left + rect.width / 2 - width / 2;
    } else {
        x = side === 'left' ? rect.left - width - offset : rect.right + offset;
        y = align === 'start' ? rect.top : align === 'end' ? rect.bottom - height : rect.top + rect.height / 2 - height / 2;
    }

    // Shift back into the viewport.
    x = Math.min(Math.max(x, padding), Math.max(padding, viewport.width - width - padding));
    y = Math.min(Math.max(y, padding), Math.max(padding, viewport.height - height - padding));

    floating.style.left = `${Math.round(x)}px`;
    floating.style.top = `${Math.round(y)}px`;
    floating.dataset.side = side;

    // Let panels cap their height to the room they have.
    floating.style.setProperty('--mellow-available-height', `${Math.max(120, space[side] - offset)}px`);

    return side;
}

/**
 * Keep a floating element positioned while it is open. Returns a cleanup function.
 */
export function autoUpdate(anchor, floating, update) {
    let frame;
    const schedule = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);

    const observer = new ResizeObserver(schedule);
    observer.observe(floating);
    if (anchor instanceof Element) observer.observe(anchor);

    update();

    return () => {
        cancelAnimationFrame(frame);
        window.removeEventListener('scroll', schedule, true);
        window.removeEventListener('resize', schedule);
        observer.disconnect();
    };
}

let counter = 0;

/** Make sure an element has an id (for ARIA references) and return it. */
export function ensureId(el, prefix = 'mellow') {
    if (!el.id) el.id = `${prefix}-${++counter}-${Math.random().toString(36).slice(2, 7)}`;
    return el.id;
}
